import { getConfig } from '../../config/runtimeConfig';
import { getNumber, getString, getStrings, Tag, type DicomJson } from '../../data/dicomTags';
import { SINGLE_FRAME_IMAGE_SOP_CLASSES, SopClass } from '../sopClasses';

// The one viewport for every image: a single-frame instance (VL Microscopic
// Image, VL Photographic "XC", secondary capture — photomicrographs, gross
// photos) goes through the same DMV engine as a pyramidal slide, so it gets the
// same canvas, controls, annotations and compare. DMV only accepts VL Whole
// Slide Microscopy Image metadata, so the instance is presented to it as a
// one-level pyramid of ONE tile: the tile is the whole image, and frame 1 is
// fetched from the real instance exactly as stored. Only the metadata handed to
// DMV changes; nothing is written anywhere.

const PIXEL_SPACING = '00280030';
// Spacing for an image without one when no synthetic spacing is configured:
// convert's synthetic value (0.25 µm/px).
const FALLBACK_SPACING_MM = 0.00025;
// ImageOrientationSlide for an image whose top-left is its origin — the same
// value convert writes (orientation.UPRIGHT).
const UPRIGHT = [0, -1, 0, 1, 0, 0];

const el = (vr: string, ...Value: unknown[]) => ({ vr, Value });

/** Whether this instance is a visible-light single image to show as a one-tile pyramid. */
export function isSingleFrameImage(ds: DicomJson | undefined): boolean {
  return (
    !!ds &&
    SINGLE_FRAME_IMAGE_SOP_CLASSES.includes(getString(ds, Tag.SOPClassUID) ?? '') &&
    (getNumber(ds, Tag.Rows) ?? 0) > 0 &&
    (getNumber(ds, Tag.Columns) ?? 0) > 0
  );
}

/**
 * The instance as a single-tile, single-level VL Whole Slide Microscopy Image.
 *
 * Pixel spacing is the image's own when it declares one (0028,0030); otherwise
 * the configured synthetic spacing, which DmvEngine recognises and so reports
 * magnification as approximate and hides the scale bar — as for any slide
 * without real spacing.
 */
export function asSingleTileWsi(ds: DicomJson): DicomJson {
  const rows = getNumber(ds, Tag.Rows) ?? 0;
  const cols = getNumber(ds, Tag.Columns) ?? 0;
  const own = getStrings(ds, PIXEL_SPACING).map(Number);
  // 0 in config only turns the synthetic guard off; DMV still needs a spacing.
  const synthetic = getConfig().syntheticPixelSpacingMm;
  const fallback = synthetic > 0 ? synthetic : FALLBACK_SPACING_MM;
  const spacing =
    own.length >= 2 && own.every((v) => Number.isFinite(v) && v > 0)
      ? own.slice(0, 2)
      : [fallback, fallback];
  // Stable per series, so anything keyed on them (annotations) survives reloads.
  const series = getString(ds, Tag.SeriesInstanceUID) ?? '';
  const imageType = ['ORIGINAL', 'PRIMARY', 'VOLUME', 'NONE'];

  return {
    ...ds,
    [Tag.SOPClassUID]: el('UI', SopClass.VLWholeSlideMicroscopyImage),
    [Tag.ImageType]: el('CS', ...imageType),
    [Tag.NumberOfFrames]: el('IS', 1),
    [Tag.FrameOfReferenceUID]: el('UI', getString(ds, Tag.FrameOfReferenceUID) ?? series),
    [Tag.ContainerIdentifier]: el('LO', getString(ds, Tag.ContainerIdentifier) ?? series),
    [Tag.TotalPixelMatrixColumns]: el('UL', cols),
    [Tag.TotalPixelMatrixRows]: el('UL', rows),
    '00480303': el('UL', 1), // TotalPixelMatrixFocalPlanes
    '00480302': el('UL', 1), // NumberOfOpticalPaths
    '00209311': el('CS', 'TILED_FULL'), // DimensionOrganizationType
    '00480102': el('DS', ...UPRIGHT), // ImageOrientationSlide
    '00480001': el('FL', cols * spacing[1]), // ImagedVolumeWidth
    '00480002': el('FL', rows * spacing[0]), // ImagedVolumeHeight
    '00480003': el('FL', 0.001), // ImagedVolumeDepth
    '00480008': el('SQ', {
      '0040072A': el('DS', 0), // XOffsetInSlideCoordinateSystem
      '0040073A': el('DS', 0), // YOffsetInSlideCoordinateSystem
    }), // TotalPixelMatrixOriginSequence
    // One brightfield path, no ICC profile: the image is shown as stored.
    [Tag.OpticalPathSequence]: el('SQ', {
      '00480106': el('SH', '1'), // OpticalPathIdentifier
      [Tag.IlluminationTypeCodeSequence]: el('SQ', {
        [Tag.CodeValue]: el('SH', '111744'),
        '00080102': el('SH', 'DCM'),
        [Tag.CodeMeaning]: el('LO', 'Brightfield illumination'),
      }),
    }),
    '52009229': el('SQ', {
      '00289110': el('SQ', {
        [PIXEL_SPACING]: el('DS', ...spacing),
        '00180050': el('DS', 0.001), // SliceThickness
      }), // PixelMeasuresSequence
      '00480207': el('SQ', { '00480106': el('SH', '1') }), // OpticalPathIdentificationSequence
      '00409310': el('SQ', { '00089007': el('CS', ...imageType) }), // WSM FrameTypeSequence
    }), // SharedFunctionalGroupsSequence
  } as DicomJson;
}
